import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Tabs, message, Popconfirm, Modal, Descriptions, Empty } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import './AdminCommon.css';

const { TabPane } = Tabs;

const categoryMap = {
  creation: '内容创作',
  distribution: '发行',
  marketing: '营销',
  opc: 'OPC',
  business: '商业服务',
  news: '信息资讯',
  data: '数据',
  general: '通用',
  other: '其他',
};

const AuditCenter = () => {
  const [activeTab, setActiveTab] = useState('skills');
  const [pendingSkills, setPendingSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingSkill, setViewingSkill] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);

  // 加载待审核技能（status=0）
  const fetchPendingSkills = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/skills?status=0&limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const list = (data.data || []).map(s => ({
          id: s.id,
          name: s.displayName || s.name,
          rawName: s.name,
          author: s.authorName || '未知',
          category: s.category,
          description: s.description,
          version: s.version,
          tags: s.tags || [],
          submittedAt: s.createdAt ? new Date(s.createdAt).toLocaleString('zh-CN') : '-',
          rawData: s,
        }));
        setPendingSkills(list);
      } else {
        message.error(data.message || '获取待审核列表失败');
      }
    } catch (e) {
      console.error('fetchPendingSkills error:', e);
      message.error('获取待审核列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingSkills();
  }, []);

  // 审核操作
  const handleAudit = async (id, action) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/admin/skills/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (data.success) {
        message.success(data.message || '操作成功');
        fetchPendingSkills();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (e) {
      console.error('audit error:', e);
      message.error('操作失败');
    }
  };

  const handleView = (record) => {
    setViewingSkill(record);
    setViewModalVisible(true);
  };

  const skillColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '技能名称', dataIndex: 'name', key: 'name' },
    { title: '作者', dataIndex: 'author', key: 'author', width: 120 },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (cat) => categoryMap[cat] || cat,
    },
    { title: '版本', dataIndex: 'version', key: 'version', width: 80 },
    { title: '提交时间', dataIndex: 'submittedAt', key: 'submittedAt', width: 160 },
    {
      title: '操作',
      key: 'action',
      width: 260,
      render: (_, record) => (
        <Space size="small">
          <Button icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>
            预览
          </Button>
          <Popconfirm
            title="确认通过此技能审核?"
            description="通过后将公开展示"
            onConfirm={() => handleAudit(record.id, 'approve')}
            okText="通过"
            cancelText="取消"
          >
            <Button icon={<CheckCircleOutlined />} size="small" type="primary">
              通过
            </Button>
          </Popconfirm>
          <Popconfirm
            title="确认拒绝此技能?"
            description="拒绝后将变为已下架状态"
            onConfirm={() => handleAudit(record.id, 'reject')}
            okText="拒绝"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<CloseCircleOutlined />} size="small" danger>
              拒绝
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">审核中心</h1>
        <p className="page-subtitle">审核技能和专家申请</p>
      </div>

      <Card
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchPendingSkills} loading={loading}>
            刷新
          </Button>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={`技能审核 (${pendingSkills.length})`} key="skills">
            <Table
              columns={skillColumns}
              dataSource={pendingSkills}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: <Empty description="暂无待审核技能" /> }}
            />
          </TabPane>
          <TabPane tab="专家审核 (0)" key="experts">
            <Empty description="专家审核功能即将上线" />
          </TabPane>
        </Tabs>
      </Card>

      {/* 预览弹窗 */}
      <Modal
        title="技能预览"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>,
          viewingSkill && (
            <Popconfirm
              key="reject"
              title="确认拒绝?"
              onConfirm={() => {
                handleAudit(viewingSkill.id, 'reject');
                setViewModalVisible(false);
              }}
              okText="拒绝"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<CloseCircleOutlined />}>拒绝</Button>
            </Popconfirm>
          ),
          viewingSkill && (
            <Popconfirm
              key="approve"
              title="确认通过?"
              onConfirm={() => {
                handleAudit(viewingSkill.id, 'approve');
                setViewModalVisible(false);
              }}
              okText="通过"
              cancelText="取消"
            >
              <Button type="primary" icon={<CheckCircleOutlined />}>通过</Button>
            </Popconfirm>
          ),
        ]}
      >
        {viewingSkill && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="ID">{viewingSkill.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color="orange">待审核</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="显示名称">{viewingSkill.name}</Descriptions.Item>
            <Descriptions.Item label="标识名">{viewingSkill.rawName}</Descriptions.Item>
            <Descriptions.Item label="作者">{viewingSkill.author}</Descriptions.Item>
            <Descriptions.Item label="分类">{categoryMap[viewingSkill.category] || viewingSkill.category}</Descriptions.Item>
            <Descriptions.Item label="版本">{viewingSkill.version || '-'}</Descriptions.Item>
            <Descriptions.Item label="提交时间">{viewingSkill.submittedAt}</Descriptions.Item>
            <Descriptions.Item label="标签" span={2}>
              {(viewingSkill.tags || []).length > 0
                ? (viewingSkill.tags || []).map(t => <Tag key={t}>{t}</Tag>)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              {viewingSkill.description || '暂无描述'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AuditCenter;
